import { useLayoutEffect, useState } from 'react';
import { Camera, Globe, Heart, Lock, MessageSquare } from 'lucide-react';
import { MemberCardHeroStat } from '@/components/profile/MemberCardHeroStat';
import { MemberCardUtilityTile } from '@/components/profile/MemberCardUtilityTile';

export interface ProfileMemberCardProps {
  handle: string;
  profilePhotoUrl: string | null;
  aboutMe: string | null;
  handicap: number | null;
  ghinConnected: boolean;
  anccIndex: string | null;
  qualifyingRounds: number;
  acesCount: number;
  givingDollars: string;
  venmoConnected: boolean;
  visibility: 'public' | 'followers';
  serviceLabel: string | null;
  clubAbbr: string;
  memberSinceYear: string;
  onEditPhoto: () => void;
  onEditAbout: () => void;
  onConnectGhin: () => void;
  onOpenAnccIndex: () => void;
  onOpenAces: () => void;
  onOpenGiving: () => void;
  onEditService: () => void;
  onOpenIdeas: () => void;
  onOpenVenmo: () => void;
  onOpenPrivacy: () => void;
}

export function ProfileMemberCard({
  handle,
  profilePhotoUrl,
  aboutMe,
  handicap,
  ghinConnected,
  anccIndex,
  qualifyingRounds,
  acesCount,
  givingDollars,
  venmoConnected,
  visibility,
  serviceLabel,
  clubAbbr,
  memberSinceYear,
  onEditPhoto,
  onEditAbout,
  onConnectGhin,
  onOpenAnccIndex,
  onOpenAces,
  onOpenGiving,
  onEditService,
  onOpenIdeas,
  onOpenVenmo,
  onOpenPrivacy,
}: ProfileMemberCardProps) {
  const [aboutExpanded, setAboutExpanded] = useState(false);

  useLayoutEffect(() => {
    setAboutExpanded(false);
  }, [aboutMe]);

  const showAboutToggle = Boolean(aboutMe && aboutMe.trim().length > 0);

  const isFollowers = visibility === 'followers';
  const hcpValue = handicap != null ? handicap.toFixed(1) : '—';
  const anccValue = anccIndex ?? '—';
  const anccQualified = qualifyingRounds >= 3 && anccIndex != null;

  return (
    <div className="relative mb-4 rounded-[14px] border-[1.5px] border-navy bg-paper shadow-[0_4px_14px_rgba(10,30,63,0.14),0_1px_2px_rgba(10,30,63,0.08)]">
      <div className="pointer-events-none absolute inset-[5px] rounded-[10px] border border-navy/35" />

      <div className="relative z-[1] px-4 pb-3 pt-3.5">
        <div className="mb-3 flex items-center justify-between border-b border-dashed border-navy/20 pb-2">
          <div className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-navy">
            MEMBER CARD
          </div>
          <div className="text-[9px] font-bold italic tracking-[0.15em] text-ink-soft">
            @{handle}
          </div>
        </div>

        <div className={`mb-3.5 ${aboutExpanded && aboutMe ? 'flex flex-col gap-2.5' : 'flex items-stretch gap-2.5'}`}>
          <div className="flex w-[125px] shrink-0 self-stretch">
            {profilePhotoUrl ? (
              <div className="relative h-full min-h-[125px] w-full overflow-hidden rounded-[14px]">
                <img
                  src={profilePhotoUrl}
                  alt=""
                  className="h-full min-h-[125px] w-full object-cover"
                />
                <button
                  type="button"
                  onClick={onEditPhoto}
                  aria-label="Change photo"
                  className="absolute right-1.5 top-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-black/40 text-paper active:scale-95"
                >
                  <Camera size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onEditPhoto}
                aria-label="Add photo"
                className="flex h-full min-h-[125px] w-full flex-col items-center justify-center gap-1 rounded-[14px] border-2 border-dashed border-navy/15 bg-cream p-2 active:scale-[0.98]"
              >
                <Camera size={22} className="text-navy" />
                <div className="text-[11px] font-bold text-navy">Add Photo</div>
              </button>
            )}
          </div>

          <div className={`flex min-w-0 flex-col rounded-xl border-[1.5px] border-navy bg-paper p-3 ${aboutExpanded && aboutMe ? 'w-full' : 'flex-1'}`}>
            <div className="mb-1.5 flex items-center justify-between">
              <div className="font-serif text-[13px] font-bold tracking-[-0.01em] text-ink">
                About Me
              </div>
              <button
                type="button"
                onClick={onEditAbout}
                className="bg-transparent px-1 py-0.5 text-[11px] font-semibold text-navy active:opacity-70"
              >
                ✎ Edit
              </button>
            </div>
            {aboutMe ? (
              <div>
                <div
                  className={`whitespace-pre-wrap break-words text-[11.5px] leading-[1.45] text-ink-soft ${
                    aboutExpanded ? '' : 'line-clamp-6'
                  }`}
                >
                  {aboutMe}
                </div>
                {showAboutToggle && (
                  <button
                    type="button"
                    onClick={() => setAboutExpanded((v) => !v)}
                    className="mt-1 bg-transparent text-[11px] font-semibold text-navy active:opacity-70"
                  >
                    {aboutExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={onEditAbout}
                className="text-left text-[11.5px] italic leading-[1.45] text-mute"
              >
                No bio yet. Tap Edit to add one.
              </button>
            )}
          </div>
        </div>

        <div className="mb-2 flex gap-2">
          <MemberCardHeroStat
            chip="HCP"
            value={hcpValue}
            label={ghinConnected ? 'GHIN Index' : 'Handicap'}
            sub={ghinConnected ? 'GHIN connected' : 'Tap to connect GHIN'}
            ariaLabel={`Handicap, ${hcpValue}, opens GHIN settings`}
            onClick={onConnectGhin}
          />
          <MemberCardHeroStat
            chip={clubAbbr}
            value={anccValue}
            label={`${clubAbbr} Index`}
            sub={anccQualified ? 'Club form' : 'Post 3 rounds'}
            ariaLabel={`${clubAbbr} Index, ${anccValue}, opens index details`}
            onClick={onOpenAnccIndex}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <MemberCardUtilityTile
            icon={<span className="text-base leading-none">🦅</span>}
            trophy
            value={acesCount > 0 ? acesCount : null}
            label={acesCount === 1 ? 'Hole-in-One' : 'Holes-in-One'}
            ariaLabel={`Aces, ${acesCount}, opens ace registry`}
            onClick={onOpenAces}
          />
          <MemberCardUtilityTile
            icon={<Heart size={14} />}
            value={givingDollars}
            label="Giving"
            ariaLabel={`Community giving, ${givingDollars}`}
            onClick={onOpenGiving}
          />
          <MemberCardUtilityTile
            icon={<span className="text-sm leading-none">🎖</span>}
            label={serviceLabel || 'Service'}
            ariaLabel={`Service, ${serviceLabel || 'not set'}`}
            onClick={onEditService}
          />
          <MemberCardUtilityTile
            icon={<MessageSquare size={14} />}
            label="Ideas"
            ariaLabel="Ideas, opens suggestion box"
            onClick={onOpenIdeas}
          />
          <MemberCardUtilityTile
            icon="V"
            value={venmoConnected ? '✓' : '—'}
            label="Venmo"
            ariaLabel={`Venmo, ${venmoConnected ? 'connected' : 'not set'}`}
            onClick={onOpenVenmo}
          />
          <MemberCardUtilityTile
            icon={isFollowers ? <Lock size={14} /> : <Globe size={14} />}
            label={isFollowers ? 'Followers' : 'Public'}
            ariaLabel={`Profile visibility, ${isFollowers ? 'followers only' : 'public'}`}
            onClick={onOpenPrivacy}
          />
        </div>

        <div className="mt-0.5 flex items-center justify-between border-t border-dashed border-navy/15 pt-2 text-[8.5px] font-bold italic tracking-[0.1em] text-ink-soft">
          <span>· {clubAbbr} MEMBER ·</span>
          <span>EST. {memberSinceYear}</span>
        </div>
      </div>
    </div>
  );
}
